import { useState, useEffect } from 'react';
import { Loader2, Search, PackageX } from 'lucide-react';
import { productAPI } from '../../../config/product-api';
import './ProductSelectionModal.css';

const ProductSelectionModal = ({
  isOpen,
  onClose,
  selectedProductIds,
  onSave,
  selectionIdKey = '_id',
  maxSelections = null,
  title = 'Select Products',
  saveButtonLabel = 'Add Products'
}) => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [tempSelectedIds, setTempSelectedIds] = useState([]);

  const getSelectionId = (product) => {
    const id = product[selectionIdKey];
    return id !== undefined && id !== null ? String(id) : String(product._id);
  };

  const normalizeIds = (ids) => (ids || []).map((id) => String(id));

  useEffect(() => {
    if (isOpen) {
      setTempSelectedIds(normalizeIds(selectedProductIds));
      setSearchQuery('');
      setProducts([]);
    }
  }, [isOpen, selectedProductIds]);

  const fetchProducts = async (query) => {
    if (!query || query.length < 3) {
      setProducts([]);
      return;
    }

    setLoading(true);
    try {
      const response = await productAPI.fetchProducts(1, query, 'all', 'all', 'all', 50);
      if (response.success) {
        setProducts(response.data.products || []);
      }
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchProducts(searchQuery);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const toggleProduct = (productId) => {
    const id = String(productId);
    if (maxSelections === 1) {
      setTempSelectedIds((prev) => (prev.includes(id) ? [] : [id]));
      return;
    }
    setTempSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  const handleSave = () => {
    onSave(tempSelectedIds);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div
      className="product-selection-modal-overlay"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="product-selection-modal-card">

        {/* Header */}
        <div className="product-selection-modal-header">
          <h2>{title}</h2>
          <button className="product-selection-modal-close" onClick={onClose}>
            &times;
          </button>
        </div>

        {/* Search */}
        <div className="product-selection-modal-search-section">
          <input
            type="text"
            className="product-selection-modal-search"
            placeholder="Enter at least 3 characters to search products..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            autoFocus
          />
        </div>

        {/* Body */}
        <div className="product-selection-modal-body">
          {loading ? (
            <div className="product-selection-modal-loading">
              <Loader2 className="chat-spin" size={24} />
              <p>Searching products...</p>
            </div>
          ) : products.length === 0 ? (
            <div className="product-selection-modal-empty">
              {searchQuery.length < 3 ? (
                <>
                  <div className="product-selection-modal-empty-icon">
                    <Search size={40} />
                  </div>
                  <p>Enter at least 3 characters to search products</p>
                </>
              ) : (
                <>
                  <div className="product-selection-modal-empty-icon">
                    <PackageX size={40} />
                  </div>
                  <p>No products found for "{searchQuery}"</p>
                </>
              )}
            </div>
          ) : (
            <div className="product-selection-modal-grid">
              {[...products].sort((a, b) => {
                const aSelected = tempSelectedIds.includes(getSelectionId(a)) ? 1 : 0;
                const bSelected = tempSelectedIds.includes(getSelectionId(b)) ? 1 : 0;
                return bSelected - aSelected;
              }).map(product => {
                const selectionId = getSelectionId(product);
                return (
                <label
                  key={product._id}
                  className={`product-selection-modal-item ${tempSelectedIds.includes(selectionId) ? 'selected' : ''}`}
                >
                  <input
                    type="checkbox"
                    className="product-selection-modal-native-checkbox"
                    checked={tempSelectedIds.includes(selectionId)}
                    onChange={() => toggleProduct(selectionId)}
                  />

                  {product.images && product.images.length > 0 ? (
                    <img
                      src={product.images[0].image}
                      alt={product.name}
                      className="product-selection-modal-image"
                    />
                  ) : product.image ? (
                    <img
                      src={product.image}
                      alt={product.name}
                      className="product-selection-modal-image"
                    />
                  ) : (
                    <div className="product-selection-modal-no-image">No Image</div>
                  )}

                  <div className="product-selection-modal-info">
                    <span className="product-selection-modal-name">{product.name}</span>
                    <div className="product-selection-modal-meta">
                      <span className="product-selection-modal-category">{product.category}</span>
                      {product.rate && (
                        <span className="product-selection-modal-rate">₹{product.rate}</span>
                      )}
                    </div>
                  </div>
                </label>
              );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="product-selection-modal-footer">
          <div className="product-selection-modal-stats">
            {tempSelectedIds.length > 0
              ? maxSelections === 1
                ? '1 product selected'
                : `${tempSelectedIds.length} product${tempSelectedIds.length !== 1 ? 's' : ''} selected`
              : 'No product selected'}
          </div>
          <div className="product-selection-modal-actions">
            <button
              className="product-selection-modal-btn product-selection-modal-btn-cancel"
              onClick={onClose}
            >
              Cancel
            </button>
            <button
              className="product-selection-modal-btn product-selection-modal-btn-save"
              onClick={handleSave}
              disabled={loading}
            >
              {saveButtonLabel}
            </button>
          </div>
        </div>

      </div>
    </div>
  );
};

export default ProductSelectionModal;